/**
 * Read Only Context
 *
 * Manages the read-only state independently from the main MasterclassContext.
 * Permission is fetched from the backend via the masterclass's hasReviewerAccess field.
 */
import { createContext, useContext, useState, ReactNode, useMemo } from 'react';

import { useGetMasterClassAccessQuery } from '@/@generated/codegen/API';
import { useMasterclass } from '@/contexts/MasterclassContext';

import { ReadOnlyContextType } from '../types';

const ReadOnlyContext = createContext<ReadOnlyContextType | undefined>(undefined);

export const ReadOnlyProvider = ({ children }: { children: ReactNode }) => {
  const [isReadOnly, setIsReadOnly] = useState(false);
  const { selectedMasterclass } = useMasterclass();

  const { data: accessData, loading: accessLoading } = useGetMasterClassAccessQuery({
    variables: {
      where: { id: selectedMasterclass || '' },
    },
    skip: !selectedMasterclass,
  });

  const hasAccessToReadOnlyMode = useMemo(() => {
    if (accessLoading || !accessData?.getMasterClass) return false;
    return accessData.getMasterClass.hasReviewerAccess;
  }, [accessData, accessLoading]);

  const setReadOnlyMode = (value: boolean) => {
    setIsReadOnly(value);
  };

  return (
    <ReadOnlyContext.Provider value={{ isReadOnly, setReadOnlyMode, hasAccessToReadOnlyMode }}>
      {children}
    </ReadOnlyContext.Provider>
  );
};

export const useReadOnlyContext = () => {
  const context = useContext(ReadOnlyContext);
  if (context === undefined) {
    throw new Error('useReadOnlyContext must be used within a ReadOnlyProvider');
  }
  return context;
};
